import {
	createUser,
	deletedUser,
	getProfils,
	updateUser,
} from '@/features/auth/api/user.api';
import { TProfil, TUser } from '@/features/auth/types';
import { ToastError, ToastGeneric } from '@/utils/toast';
import { Icon } from '@iconify/react';
import { HighlightOffRounded } from '@mui/icons-material';
import {
	Box,
	Button,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from '@mui/material';
import Grow from '@mui/material/Grow';
import Modal from '@mui/material/Modal';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { Loader } from '../Loader/Loader';
import './ModalUser.styles.scss';

type TNewUserProps = {
	open: boolean;
	user_id?: string;
	isForUpdateOrDelete?: Number;
	currentUser?: TUser;
	onClose:
		| ((event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void)
		| undefined;
	closeClick: () => void;
	refreshUserListe?: () => void;
};

const CModalUser: React.FC<TNewUserProps> = ({
	open,
	user_id,
	currentUser,
	onClose,
	closeClick,
	refreshUserListe,
	isForUpdateOrDelete,
}) => {
	// Les variable de view password
	const [showPassword, setShowPassword] = React.useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(false);
	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};
	const [showPasswordConfirm, setShowPasswordConfirm] =
		React.useState<boolean>(false);
	const handleClickShowPasswordConfirm = () => {
		setShowPasswordConfirm(!showPasswordConfirm);
	};

	const [profilsData, setProfilsData] = useState<TProfil[]>([]);
	// const [userData, setUserData] = useState<TUser | {} >({});

	/**
	 *  Gestion du formulaire d'ajout d'un utilisateur
	 */
	const validationSchema = yup.object({
		nom: yup.string().required('Nom est obligatoire'),
		prenoms: yup.string().required('Required'),
		email: yup
			.string()
			.email('Adresse email invalide')
			.required('Adresse email obligatoire'),
		profil_id: yup.string().required('Required'),
		telephone: yup.number().required('Required'),
	});

	const formik = useFormik({
		initialValues: {
			nom: currentUser?.nom || '',
			prenoms: currentUser?.prenoms || '',
			email: currentUser?.email || '',
			profil_id: currentUser?.profil_id || '',
			telephone: currentUser?.telephone || '',
		},
		validationSchema: validationSchema,
		onSubmit: (values: any) => {
			setIsLoading(true);
			if (user_id && isForUpdateOrDelete === 1) {
				updateUserFunction(values);
			} else {
				createUser(values)
					.then(response => {
						console.log('response', response);
						setIsLoading(false);
						if (response.error) {
							ToastError.fire({
								text: response.message,
								timer: 9000,
							});
						} else {
							closeClick();
							refreshUserListe?.();
							ToastGeneric(
								'Opération effectuée avec succès',
								'success',
								3000
							).fire();
							console.table(response);
						}
					})
					.catch(error => {
						setIsLoading(false);
					});
			}
		},
	});

	// Récupération des profils utilisateurs
	const getUserProfils = async () => {
		const { data } = await getProfils();
		const dataWithNewObjectKeys: TProfil[] = data.map((profil: any) => {
			return {
				id: profil.id,
				label: profil.libelle,
			};
		});

		// console.log("dataWithNewObjectKeys", dataWithNewObjectKeys);
		setProfilsData(dataWithNewObjectKeys);
	};

	// Fonction de mise à jour des utilisateurs
	const updateUserFunction = async (values: TUser) => {
		let id = currentUser?.id!;
		const toSend = {
			id: currentUser?.id,
			nom: values?.nom,
			prenoms: values?.prenoms,
			email: values?.email,
			profil_id: values?.profil_id,
			telephone: values?.telephone,
		};
		updateUser(id, toSend)
			.then(response => {
				setIsLoading(false);
				if (response.error) {
					ToastError.fire({ text: response.message, timer: 9000 });
				} else {
					ToastGeneric(
						'Compte modifié avec succès',
						'success',
						3000
					).fire();
					refreshUserListe?.();
					closeClick();
				}
			})
			.catch(error => {
				setIsLoading(false);
			});
	};

	// Fonction de suppression des utilisateurs
	const deleteUserFunction = async (values: string) => {
		let id = currentUser?.id!;
		deletedUser(id).then(response => {
			if (response.error) {
				ToastError.fire({ text: response.message, timer: 9000 });
			} else {
				ToastGeneric(
					'Compte supprimé avec succès',
					'success',
					3000
				).fire();
				refreshUserListe?.();
				closeClick();
			}
		});
	};

	// Envoie des données de profil et reinitailisation du formulaire
	useEffect(() => {
		getUserProfils();

		if (open && !user_id) {
			formik.setValues({
				email: '',
				nom: '',
				prenoms: '',
				profil_id: '',
				telephone: '',
			});
		}
	}, [open, user_id]);

	// Envoie des données de l'utilisateur dans le formik form
	useEffect(() => {
		if (currentUser && isForUpdateOrDelete === 1) {
			formik.setValues({
				email: currentUser.email,
				nom: currentUser.nom,
				prenoms: currentUser.prenoms,
				profil_id: currentUser.profil_id!,
				telephone: currentUser.telephone!,
			});
		}
		if (currentUser && isForUpdateOrDelete === 2) {
			deleteUserFunction(currentUser.id!);
		}
	}, [currentUser, user_id]);

	return (
		<Modal
			aria-labelledby="transition-modal-title"
			aria-describedby="transition-modal-description"
			open={open}
			onClose={onClose}
			closeAfterTransition
		>
			<Grow in={open}>
				<div className="c-modal ">
					<IconButton
						className="close-icon"
						size="large"
						color="inherit"
						onClick={closeClick}
					>
						<HighlightOffRounded />
					</IconButton>
					{/* <Icon className="fs-new-user-icon" icon="bxs:user-plus" /> */}
					<div className="header-part">
						<h3 className="titl uppercase">
							{user_id
								? "Modifier l'utilisateur"
								: 'Ajouter un utilisateur'}
						</h3>
						{/* <p>{user_id && `userID : ${user_id}`}</p> */}
					</div>

					<form className="form-part" onSubmit={formik.handleSubmit}>
						<Loader isLoading={isLoading} />
						<Box>
							<Grid container spacing={3} columnSpacing={3}>
								<Grid item xs={12} sm={12} lg={6}>
									<TextField
										placeholder="Votre nom"
										variant="outlined"
										label="Nom"
										value={formik.values.nom}
										onChange={formik.handleChange}
										name="nom"
										type="text"
									/>
								</Grid>
								<Grid item xs={12} sm={12} lg={6}>
									<TextField
										placeholder="Votre prénoms"
										variant="outlined"
										label="Prénoms"
										value={formik.values.prenoms}
										onChange={formik.handleChange}
										name="prenoms"
										type="text"
									/>
								</Grid>
								<Grid item xs={12} sm={12} lg={12}>
									<TextField
										placeholder="Votre adresse e-mail"
										id="outlined-disabled"
										label="Adresse e-mail"
										value={formik.values.email}
										onChange={formik.handleChange}
										name="email"
										type="email"
									/>
								</Grid>
								<Grid item xs={12} sm={12} lg={6}>
									<FormControl fullWidth>
										<InputLabel id="demo-simple-select-label">
											Profil
										</InputLabel>
										<Select
											labelId="demo-simple-select-label"
											id="demo-simple-select"
											label="Profil"
											value={formik.values.profil_id}
											onChange={formik.handleChange}
											name="profil_id"
										>
											{profilsData.map(item => (
												<MenuItem
													key={item.id}
													value={item.id}
												>
													{item.label}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
								<Grid item xs={12} sm={12} lg={6}>
									<TextField
										placeholder="Votre contact"
										variant="outlined"
										label="Contact"
										value={formik.values.telephone}
										onChange={formik.handleChange}
										name="telephone"
										type="text"
									/>
								</Grid>
								<Grid item xs={12} sm={12} lg={6}>
									<Button
										variant="contained"
										className="filter-button "
										type="reset"
										sx={{ marginTop: '30px' }}
										size="large"
										startIcon={
											<Icon icon="clarity:times-circle-solid" />
										}
										onClick={closeClick}
									>
										Annuler
									</Button>
								</Grid>
								<Grid item xs={12} sm={12} lg={6}>
									<Button
										variant="contained"
										className="filter-button reset"
										type="submit"
										color="inherit"
										sx={{ marginTop: '30px' }}
										size="large"
										startIcon={
											<Icon icon="ant-design:save-filled" />
										}
									>
										{user_id ? 'Modifier' : 'Enregistrer'}
									</Button>
								</Grid>
							</Grid>
						</Box>
					</form>
				</div>
			</Grow>
		</Modal>
	);
};

export default CModalUser;
